import {Colors, Lightning} from "@lightningjs/sdk";
import { ChannelListHorizontal } from "./components/ChannelListHorizontal/ChannelListHorizontal";
import { LiveVideoPlayer } from "./components/LiveVideoPlayer/LiveVideoPlayer";
import { NowPlayingBanner, BANNER_H, BANNER_H_PREVIEW } from "./components/NowPlayingBanner/NowPlayingBanner";
import { CategoryList } from "./components/CategoryList/CategoryList";
import { WelcomeBox, WELCOME_BOX_W, WELCOME_BOX_H } from "./components/WelcomeBox/WelcomeBox";
import { ChannelItemModel } from "../../lib/models";
import theme from "../../lib/theme";
import { MENU_WIDTH } from "../../lib/utils";
import { LiveChannelsBase } from "./LiveChannelsBase";
import { LiveTvGroup } from "./components/ChannelListBase/ChannelListBase";
import { CATEGORY_ITEM_H } from "./components/CategoryItem/CategoryItem";

const LIST_H = theme.channelItem.h + 60;
const CATEGORY_GAP = 12;
const BG_TOP_Y = theme.layout.h - Math.max(LIST_H + CATEGORY_ITEM_H + CATEGORY_GAP + 20);
const BOX_GAP = 36;
// WelcomeBox sits at MENU_WIDTH+28; channels/categories shift right to make room
const LIST_START_X = MENU_WIDTH + 28 + WELCOME_BOX_W + BOX_GAP;
const WELCOME_X_VISIBLE = MENU_WIDTH + 28;
const WELCOME_X_HIDDEN = MENU_WIDTH + 28 - WELCOME_BOX_W - BOX_GAP;

interface LiveChannelsHorizontalTemplateSpec extends Lightning.Component.TemplateSpec {
    VideoPlayer: typeof LiveVideoPlayer;
    ListBackground: object;
    WelcomeBox: typeof WelcomeBox;
    CategoryList: typeof CategoryList;
    List: typeof ChannelListHorizontal;
    NowPlayingBanner: typeof NowPlayingBanner;
}

interface LiveChannelsTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

export class LiveChannelsHorizontal
    extends LiveChannelsBase
    implements Lightning.Component.ImplementTemplateSpec<LiveChannelsHorizontalTemplateSpec>
{
    readonly VideoPlayer = this.getByRef("VideoPlayer")!;
    readonly ListBackground = this.getByRef("ListBackground")!;
    readonly WelcomeBox = this.getByRef("WelcomeBox")!;
    readonly CategoryList = this.getByRef("CategoryList")!;
    readonly List = this.getByRef("List")!;
    readonly NowPlayingBanner = this.getByRef("NowPlayingBanner")!;

    private _hasCategories = false;
    private _previewChannel: ChannelItemModel | null = null;

    static override _template(): Lightning.Component.Template<LiveChannelsHorizontalTemplateSpec> {
        return {
            w: theme.layout.w,
            h: theme.layout.h,
            VideoPlayer: {
                type: LiveVideoPlayer
            },
            ListBackground: {
                x: 0,
                y: BG_TOP_Y,
                w: theme.layout.w,
                h: theme.layout.h - BG_TOP_Y,
                rect: true,
                color: Colors(theme.colors.bg.primary).alpha(0.8).get(),
                alpha: 1,
                transitions: { alpha: { duration: 0.2 } }
            },
            WelcomeBox: {
                type: WelcomeBox,
                x: WELCOME_X_VISIBLE,
                y: theme.layout.h - WELCOME_BOX_H - 60,
                transitions: { x: { duration: 0.3, timingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" } }
            },
            CategoryList: {
                type: CategoryList,
                alpha: 0,
                x: LIST_START_X,
                y: theme.layout.h - LIST_H - CATEGORY_ITEM_H - CATEGORY_GAP,
                w: theme.layout.w - LIST_START_X + WELCOME_BOX_W + 28,
                h: CATEGORY_ITEM_H,
                transitions: { x: { duration: 0.3, timingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" } }
            },
            List: {
                type: ChannelListHorizontal,
                x: LIST_START_X,
                y: theme.layout.h - LIST_H,
                w: theme.layout.w - LIST_START_X + WELCOME_BOX_W ,
                h: LIST_H,
                transitions: { x: { duration: 0.3, timingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" } }
            },
            NowPlayingBanner: {
                type: NowPlayingBanner,
                alpha: 0,
                x: 0,
                y: theme.layout.h - BANNER_H
            }
        };
    }

    protected override get _videoPlayer(): LiveVideoPlayer {
        return this.VideoPlayer;
    }

    protected override get _channelList(): ChannelListHorizontal {
        return this.List;
    }

    override _firstActive() {
        super._firstActive();
        this._setState("WelcomeBoxState");
    }

    override _active() {
        if (window.selectedChannel) {
            const found = this._channelList.items.find(
                (item: any) => !item.isGroupTitle && (item.item?.name === window.selectedChannel?.name || item?.name === window.selectedChannel?.name)
            );
            const channelItem = found ?? this._getFirstChannelItem();
            if (channelItem) {
                const index = this._channelList.items.findIndex(
                    (item: any) => item === channelItem
                );
                if (index >= 0) this._channelList.index = index;
                this.application.emit("hideMenu");
                this._hideList();
                this._playChannel(channelItem.item);
                this._triggerOverlay(channelItem.item);
            }
            return;
        }
        super._active();
    }

    override set groups(liveTvGroups: LiveTvGroup[]) {
        this._channelList.addGroups(liveTvGroups);
        if (liveTvGroups.length > 0) {
            this._hasCategories = true;
            const names = liveTvGroups.map((g) => g.name);
            this.CategoryList.setGroups(names);
            this.CategoryList.setSelectedCategory(names[0]);
            this.CategoryList.patch({ smooth: { alpha: 1 } });
        }
    }

    protected override _showOverlay(channel: ChannelItemModel): void {
        this.NowPlayingBanner.item = channel;
        this.NowPlayingBanner.patch({
            smooth: { alpha: 1, h: BANNER_H, y: theme.layout.h - BANNER_H }
        });
    }

    protected override _hideOverlay(): void {
        this.NowPlayingBanner.patch({
            smooth: { alpha: 0, h: BANNER_H, y: theme.layout.h - BANNER_H }
        });
    }

    protected override _showList(): void {
        this._previewChannel = null;
        super._showList();
        this.application.emit("showMenu");
        this.ListBackground.patch({ smooth: { alpha: 1 } });
        this.WelcomeBox.patch({ smooth: { alpha: 1 } });
        this.List.patch({ smooth: { alpha: 1 } });
        if (this._hasCategories) {
            this.CategoryList.patch({ smooth: { alpha: 1 } });
        }
        this._setState("ChannelListState");
    }

    protected override _hideList(): void {
        super._hideList();
        this.ListBackground.patch({ smooth: { alpha: 0 } });
        this.List.patch({ smooth: { alpha: 0 } });
        this.CategoryList.patch({ smooth: { alpha: 0 } });
        this.List.hidePreview();
        this.WelcomeBox.patch({ smooth: { alpha: 0 } });
        this._setState("VideoState");
    }

    $onCategorySelected(name: string) {
        this.List.scrollToGroup(name);
        this.CategoryList.setSelectedCategory(name);
        this._setState("ChannelListState");
    }

    $onChannelGroupChanged(name: string) {
        this.CategoryList.setSelectedCategory(name);
    }

    $onChannelIndexChanged(index: number) {
        if (this._showingList) {
            this._updateWelcomeBoxForIndex(index);
        }
    }

    private _updateWelcomeBoxForIndex(index: number) {
        if (index > 1) {
            this._hideWelcomeBox();
        } else {
            this._showWelcomeBox();
        }
    }

    // When WelcomeBox is focused, show it and move lists to the right
    private _showWelcomeBox() {
        this.WelcomeBox.setSmooth("x", WELCOME_X_VISIBLE);
        this.List.setSmooth("x", LIST_START_X);
        this.CategoryList.setSmooth("x", LIST_START_X);
    }

    // When ChannelList or CategoryList is focused, hide WelcomeBox and move lists left
    private _hideWelcomeBox() {
        this.WelcomeBox.setSmooth("x", WELCOME_X_HIDDEN);
        this.List.setSmooth("x", MENU_WIDTH + 28);
        this.CategoryList.setSmooth("x", MENU_WIDTH + 28);
    }

    protected override _onChannelPreviewPrev(): void {
        const channel = this._channelList.navigatePrev();
        if (channel) {
            this._previewChannel = channel;
            this.NowPlayingBanner.previewItem = channel;
            this.NowPlayingBanner.patch({
                smooth: { alpha: 1, h: BANNER_H_PREVIEW, y: theme.layout.h - BANNER_H_PREVIEW }
            });
        }
    }

    protected override _onChannelPreviewNext(): void {
        const channel = this._channelList.navigateNext();
        if (channel) {
            this._previewChannel = channel;
            this.NowPlayingBanner.previewItem = channel;
            this.NowPlayingBanner.patch({
                smooth: { alpha: 1, h: BANNER_H_PREVIEW, y: theme.layout.h - BANNER_H_PREVIEW }
            });
        }
    }

    override _handleEnter() {
    }

    $nowPlayingBannerSelect(channel: ChannelItemModel) {
        this._previewChannel = null;
        this._playChannel(channel);
        this._triggerOverlay(channel);
    }

    static override _states() {
        return [
            class VideoState extends this {
                override _getFocused() {
                    return this._videoPlayer;
                }

                override _handleEnter() {
                    if (this._previewChannel) {
                        const channel = this._previewChannel;
                        this._previewChannel = null;
                        this._playChannel(channel);
                        this._triggerOverlay(channel);
                    } else if (this._currentChannel) {
                        this._triggerOverlay(this._currentChannel);
                    }
                }

                override _handleLeft() {
                    this._cancelOverlayTimeout();
                    this._previewChannel = null;
                    const channel = this._channelList.navigatePrev();
                    if (channel) {
                        this._playChannel(channel);
                        this._triggerOverlay(channel);
                    } else {
                        // wrap to last channel
                        const last = this._channelList.navigateLast();
                        if (last) {
                            this._playChannel(last);
                            this._triggerOverlay(last);
                        }
                    }
                    return true;
                }

                override _handleRight() {
                    this._cancelOverlayTimeout();
                    this._previewChannel = null;
                    this._onChannelNext();
                    return true;
                }

                override _handleUp() {
                    this._onChannelPreviewPrev();
                    this._startOverlayTimeout();
                    return true;
                }

                override _handleDown() {
                    this._onChannelPreviewNext();
                    this._startOverlayTimeout();
                    return true;
                }
            },

            class WelcomeBoxState extends this {
                override $enter() {
                    this._showWelcomeBox();
                }

                override _getFocused() {
                    return this.WelcomeBox;
                }

                override _handleRight() {
                    this._setState("ChannelListState");
                    return true;
                }

                override _handleDown() {
                    this._setState("ChannelListState");
                    return true;
                }

                override _handleUp() {
                    if (this._hasCategories) {
                        this._setState("CategoryState");
                    }
                    return true;
                }
            },

            class ChannelListState extends this {
                override $enter() {
                    this._updateWelcomeBoxForIndex(this._channelList.index);
                }

                override _getFocused() {
                    return this._channelList;
                }

                override _handleBack() {
                    if (this._channelList.index > 0) {
                        this._channelList.index = 0;
                        return true;
                    }

                    if (this._channelList.index === 0) {
                        this._setState("WelcomeBoxState");
                        return true;
                    }
                    this._onChannelPrev();
                    return true;
                }

                override _handleLeft() {
                    if (this._channelList.index === 0) {
                        this._setState("WelcomeBoxState");
                        return true;
                    }
                    this._onChannelPrev();
                    return true;
                }

                override _handleRight() {
                    this._onChannelNext();
                    return true;
                }

                override _handleUp() {
                    if (this._hasCategories) {
                        this._setState("CategoryState");
                    }
                    return true;
                }

                override _handleDown() {
                    return true;
                }
            },

            class CategoryState extends this {
                override $enter() {
                    this._hideWelcomeBox();
                    this.List.hidePreview();
                }

                override _getFocused() {
                    return this.CategoryList;
                }

                override _handleDown() {
                    this._setState("ChannelListState");
                    return true;
                }

                override _handleBack() {
                    if (this.CategoryList.index > 0) {
                        this.CategoryList.scrollToFirst();
                        return true;
                    }
                    this._setState("ChannelListState");
                    return true;
                }

                override _handleLeft() {
                    if (this._channelList.index === 0) {
                        this._setState("WelcomeBoxState");
                        return true;
                    }
                    return true;
                }

                override _handleRight() {
                    return true;
                }
            }
        ];
    }
}
